import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  Chip,
  OutlinedInput,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Paper,
  Stack,
  Link,
  Alert,
  FormHelperText,
  Tooltip,
  Menu,
  ListItemIcon,
  Tabs,
  Tab,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Checkbox,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Topic as SubcategoryIcon,
  Category as CategoryIcon,
  MenuBook as TitleIcon,
  ViewList as OutlineIcon,
  ArrowForward as ArrowForwardIcon,
  School as IltIcon,
  PlayLesson as SptIcon,
  DragIndicator as DragIcon,
  Visibility as ViewIcon,
  LibraryBooks as LibraryIcon,
  FreeBreakfast as BreakIcon,
  Quiz as ExamOutlineIcon,
  Videocam as VideoCallIcon,
  NoteAdd as MaterialAddIcon,
  AddCircleOutline as PlaceholderIcon,
  ContentCopy as NewEditionIcon,
  LinkOff as ClearAttachIcon,
  Remove as CollapseSlotIcon,
} from '@mui/icons-material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { enUS } from 'date-fns/locale';
import { format, isValid, parse } from 'date-fns';
import { toast } from 'react-toastify';
import api from '../../utils/api';
import { TRAINING_MATERIAL_CONTENT_TYPES, CONTENT_TYPES_NEEDING_URL } from './trainingContentTypes';

/** Mon–Sun display order; values match API (0=Sun … 6=Sat) */
const WEEKDAY_CHIPS = [
  { v: 1, short: 'Mon', long: 'Monday' },
  { v: 2, short: 'Tue', long: 'Tuesday' },
  { v: 3, short: 'Wed', long: 'Wednesday' },
  { v: 4, short: 'Thu', long: 'Thursday' },
  { v: 5, short: 'Fri', long: 'Friday' },
  { v: 6, short: 'Sat', long: 'Saturday' },
  { v: 0, short: 'Sun', long: 'Sunday' }
];

const CONTENT_TYPE_LABELS = Object.fromEntries(TRAINING_MATERIAL_CONTENT_TYPES.map((x) => [x.value, x.label]));

const SCHEDULE_MODELS = [
  { v: 'none', l: 'None (flex / self-paced window)' },
  { v: 'daily', l: 'Daily' },
  { v: 'weekly', l: 'Weekly' },
  { v: 'monthly', l: 'Monthly' },
  { v: 'yearly', l: 'Yearly' }
];

function weekdaysFromTitle(t) {
  if (Array.isArray(t.weekdays) && t.weekdays.length) {
    return [...new Set(t.weekdays.map((n) => Number(n)))].filter((n) => n >= 0 && n <= 6).sort((a, b) => a - b);
  }
  if (t.weekday != null && t.weekday !== '') return [Number(t.weekday)];
  return [];
}

function formatWeeklyDaysLabel(t) {
  const nums = weekdaysFromTitle(t);
  if (!nums.length) return '';
  const map = Object.fromEntries(WEEKDAY_CHIPS.map((d) => [d.v, d.short]));
  return nums.map((n) => map[n] || n).join(' · ');
}

const emptyTitleForm = () => ({
  name: '',
  delivery_type: 'SPT',
  start_date: '',
  end_date: '',
  schedule_model: 'none',
  weekdays: [],
  session_start_time: '',
  session_end_time: '',
  owner_user_id: '',
  instructor_user_ids: [],
  status: 'draft',
  sort_order: 0,
  outline_format: 'sequence',
  allow_drop_from_enrollment: false,
  drop_window_days: 14,
  max_enroll_drop_cycles: 1
});

const formatApiDate = (v) => {
  if (!v) return '';
  const s = String(v);
  return s.length >= 10 ? s.slice(0, 10) : s;
};

const formatApiTime = (v) => {
  if (!v) return '';
  return String(v).slice(0, 5);
};

const OUTLINE_FORMAT_TABS = [
  { value: 'sequence', label: 'Sequence Format', slotNoun: null },
  { value: 'daily', label: 'Daily Format', slotNoun: 'Day' },
  { value: 'weekly', label: 'Weekly Format', slotNoun: 'Week' },
  { value: 'monthly', label: 'Monthly Format', slotNoun: 'Month' },
  { value: 'conference', label: 'Conference Format', slotNoun: 'Session' },
  { value: 'group_discussion', label: 'Group Discussion', slotNoun: 'Topic' }
];

const COURSE_TAB_SX = {
  course: {
    minHeight: 42,
    textTransform: 'none',
    fontWeight: 600,
    bgcolor: '#E3F2FD',
    color: '#1565C0',
    borderRadius: '8px 8px 0 0',
    mx: 0.25,
    '&.Mui-selected': { bgcolor: '#1565C0', color: '#fff' }
  },
  edition: {
    minHeight: 40,
    textTransform: 'none',
    fontWeight: 600,
    bgcolor: '#FFF3E0',
    color: '#E65100',
    borderRadius: '8px 8px 0 0',
    mx: 0.25,
    '&.Mui-selected': { bgcolor: '#EF6C00', color: '#fff' }
  },
  workspace: {
    minHeight: 44,
    textTransform: 'none',
    fontWeight: 700,
    bgcolor: '#E8F5E9',
    color: '#2E7D32',
    borderRadius: '8px 8px 0 0',
    mx: 0.5,
    px: 2,
    '&.Mui-selected': { bgcolor: '#2E7D32', color: '#fff' }
  },
  outline: {
    minHeight: 44,
    textTransform: 'none',
    fontWeight: 700,
    bgcolor: '#F3E5F5',
    color: '#6A1B9A',
    borderRadius: '8px 8px 0 0',
    mx: 0.5,
    px: 2,
    '&.Mui-selected': { bgcolor: '#6A1B9A', color: '#fff' }
  }
};

/** Same field shape for Add and Edit. */
const titleFormFromRecord = (t) => {
  if (!t) return emptyTitleForm();
  const delivery = t.delivery_type === 'ILT' ? 'ILT' : 'SPT';
  return {
    name: t.name || '',
    delivery_type: delivery,
    start_date: formatApiDate(t.start_date),
    end_date: formatApiDate(t.end_date),
    schedule_model: delivery === 'SPT' ? 'none' : t.schedule_model || 'weekly',
    weekdays: weekdaysFromTitle(t),
    session_start_time: formatApiTime(t.session_start_time),
    session_end_time: formatApiTime(t.session_end_time),
    owner_user_id: t.owner_user_id || '',
    instructor_user_ids: (t.instructorLinks || []).map((x) => x.user_id).filter(Boolean),
    status: t.status || 'draft',
    sort_order: t.sort_order != null ? Number(t.sort_order) : 0,
    outline_format: OUTLINE_FORMAT_TABS.some((x) => x.value === t.outline_format)
      ? t.outline_format
      : 'sequence',
    allow_drop_from_enrollment: !!t.allow_drop_from_enrollment,
    drop_window_days: t.drop_window_days != null ? Number(t.drop_window_days) : 14,
    max_enroll_drop_cycles: t.max_enroll_drop_cycles != null ? Number(t.max_enroll_drop_cycles) : 1
  };
};

const STATUS_LABELS = {
  draft: 'Draft',
  ready_to_preview: 'Ready to preview',
  published: 'Published',
  archived: 'Archived',
  admin_blocked: 'Admin Blocked it'
};

const formatCourseEdition = (n) => {
  if (n == null || Number(n) < 1) return null;
  return `V${String(Math.min(9999, Number(n))).padStart(4, '0')}`;
};

const parseFormDate = (value) => {
  if (!value) return null;
  const d = parse(value, 'yyyy-MM-dd', new Date());
  return isValid(d) ? d : null;
};

const formatFormDate = (d) => (d && isValid(d) ? format(d, 'yyyy-MM-dd') : '');

const parseFormTime = (value) => {
  if (!value) return null;
  const d = parse(value, 'HH:mm', new Date());
  return isValid(d) ? d : null;
};

const formatFormTime = (d) => (d && isValid(d) ? format(d, 'HH:mm') : '');

/** Top-level outline rows (slots). Child items share parent_item_id. */
const outlineAnchors = (items) =>
  [...(items || [])]
    .filter((it) => !it.parent_item_id)
    .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

const slotChildren = (items, anchorId) =>
  (items || []).filter((it) => it.parent_item_id === anchorId);

const isEmptyPlaceholderSlot = (anchor, items) => {
  if (!anchor || anchor.parent_item_id) return false;
  if (slotChildren(items, anchor.id).length > 0) return false;
  if (anchor.material_id || anchor.exam_id || anchor.content_url) return false;
  return anchor.item_type === 'placeholder' || anchor.is_placeholder;
};

const getSlotAttachments = (items, anchor) => {
  const children = slotChildren(items, anchor.id);
  if (isEmptyPlaceholderSlot(anchor, items)) return children;
  return [anchor, ...children];
};

const formatEditionVersionLabel = (editions) => {
  const n = editions.length;
  const published = editions.filter((e) => e.status === 'published').length;
  const inReview = editions.filter((e) => e.status === 'draft' || e.status === 'ready_to_preview').length;
  const base = `${n} version${n === 1 ? '' : 's'}`;
  const parts = [];
  if (published > 0) parts.push(`${published} published`);
  if (inReview > 0) parts.push(`${inReview} draft/review`);
  return parts.length ? `${base} (${parts.join(', ')})` : base;
};

const collectOutlineMaterialIds = (items) => {
  const ids = new Set();
  (items || []).forEach((it) => {
    if (it.material_id) ids.add(it.material_id);
  });
  return ids;
};

const collectOutlineExamIds = (items) => {
  const ids = new Set();
  (items || []).forEach((it) => {
    if (it.exam_id) ids.add(it.exam_id);
  });
  return ids;
};

/** Group editions that share version_group_id under one course row. */
const groupTitlesByVersionGroup = (titles) => {
  const map = new Map();
  (titles || []).forEach((t) => {
    const gid = t.version_group_id || t.id;
    if (!map.has(gid)) {
      map.set(gid, { groupId: gid, name: t.name, course_code: t.course_code, editions: [] });
    }
    const g = map.get(gid);
    g.editions.push(t);
    if (t.course_code) g.course_code = t.course_code;
  });
  return Array.from(map.values()).map((g) => {
    g.editions.sort((a, b) => (Number(a.edition_number) || 0) - (Number(b.edition_number) || 0));
    const latest = g.editions[g.editions.length - 1];
    g.name = latest?.name || g.name;
    g.delivery_type = latest?.delivery_type;
    return g;
  });
};

const SECTION_SUBCAT_SX = {
  borderRadius: 2,
  mb: 2,
  bgcolor: '#E8F4FC',
  border: '1px solid #B8D9F0',
  boxShadow: 'none'
};

const SECTION_COURSES_SX = {
  borderRadius: 2,
  mb: 2,
  bgcolor: '#EEF8F1',
  border: '1px solid #B5DFC8',
  boxShadow: 'none'
};

const LearningSpotsCourseAdmin = ({
  categories,
  materials,
  users,
  onOpenCategoriesTab,
  onOpenMaterialsTab,
  onMaterialsRefresh,
  focusCategoryId = '',
  focusSpotId = '',
}) => {
  const navigate = useNavigate();
  const [categoryId, setCategoryId] = useState('');
  const [spots, setSpots] = useState([]);
  const [loadingSpots, setLoadingSpots] = useState(false);
  const [spotDialog, setSpotDialog] = useState(false);
  const [spotForm, setSpotForm] = useState({ name: '', description: '', sort_order: 0 });
  const [editingSpotId, setEditingSpotId] = useState(null);

  const [selectedSpot, setSelectedSpot] = useState(null);
  const [titles, setTitles] = useState([]);
  const [loadingTitles, setLoadingTitles] = useState(false);
  const [titleDialog, setTitleDialog] = useState(false);
  const [titleForm, setTitleForm] = useState(emptyTitleForm);
  const [editingTitleId, setEditingTitleId] = useState(null);

  const [detailTitle, setDetailTitle] = useState(null);
  const [expandedTitleId, setExpandedTitleId] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  /** free = stacked course → editions; tabbed = course tabs then edition tabs */
  const [coursesLayout, setCoursesLayout] = useState('free');
  const [selectedCourseGroupId, setSelectedCourseGroupId] = useState('');
  /** details | outline — outline hidden until user opens it */
  const [courseWorkspaceTab, setCourseWorkspaceTab] = useState('details');
  const [previewOpen, setPreviewOpen] = useState(false);

  const [breakDialog, setBreakDialog] = useState(false);
  const [breakInsertAfter, setBreakInsertAfter] = useState(null);
  const [breakForm, setBreakForm] = useState({ duration_value: 1, duration_unit: 'days', label: 'Break' });
  const [linkMatDialog, setLinkMatDialog] = useState(false);
  const [linkMaterialId, setLinkMaterialId] = useState('');
  const [linkInsertAfter, setLinkInsertAfter] = useState(null);

  const [inlineMatOpen, setInlineMatOpen] = useState(false);
  const [inlineInsertAfter, setInlineInsertAfter] = useState(null);
  const [matForm, setMatForm] = useState({
    title: '',
    description: '',
    content_type: 'youtube',
    content_url: '',
    duration_minutes: '',
    difficulty_level: 'beginner'
  });
  const [matFile, setMatFile] = useState(null);

  const [editOutlineItem, setEditOutlineItem] = useState(null);
  const [editOutlineLabel, setEditOutlineLabel] = useState('');

  const [examDialog, setExamDialog] = useState(false);
  const [examInsertAfter, setExamInsertAfter] = useState(null);
  const [examList, setExamList] = useState([]);
  const [selectedExamId, setSelectedExamId] = useState('');

  const [dragRowIdx, setDragRowIdx] = useState(null);
  const [addMenuAnchor, setAddMenuAnchor] = useState(null);
  const [addMenuAfterIdx, setAddMenuAfterIdx] = useState(null);
  /** When set, Add/Link fills this outline row in place (placeholder) instead of inserting a new line. */
  const [attachTargetItemId, setAttachTargetItemId] = useState(null);
  /** Parent slot row when adding another attachment to an already-filled sequence. */
  const [slotAnchorId, setSlotAnchorId] = useState(null);
  const [expandedSlotIds, setExpandedSlotIds] = useState({});
  const [publishedEditLock, setPublishedEditLock] = useState({ open: false, title: null });
  const [outlineFormatTab, setOutlineFormatTab] = useState('sequence');
  const [scheduleSlotDraft, setScheduleSlotDraft] = useState('1');

  const [portalFeedbackRows, setPortalFeedbackRows] = useState([]);
  const [portalFeedbackLoading, setPortalFeedbackLoading] = useState(false);

  const loadPortalFeedback = useCallback(async () => {
    if (!detailTitle?.id) {
      setPortalFeedbackRows([]);
      return;
    }
    setPortalFeedbackLoading(true);
    try {
      const r = await api.get(`/training/training-titles/${detailTitle.id}/public-feedback`);
      setPortalFeedbackRows(Array.isArray(r.data?.data) ? r.data.data : []);
    } catch {
      setPortalFeedbackRows([]);
    } finally {
      setPortalFeedbackLoading(false);
    }
  }, [detailTitle?.id]);

  useEffect(() => {
    loadPortalFeedback();
  }, [loadPortalFeedback]);

  const reviewPortalFeedback = async (feedbackId, reviewStatus) => {
    if (!detailTitle?.id) return;
    try {
      await api.patch(`/training/training-titles/${detailTitle.id}/public-feedback/${feedbackId}`, {
        review_status: reviewStatus
      });
      toast.success(reviewStatus === 'accepted' ? 'Feedback accepted.' : 'Feedback rejected.');
      loadPortalFeedback();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Could not update feedback.');
    }
  };

  const loadSpots = useCallback(async () => {
    if (!categoryId) {
      setSpots([]);
      return;
    }
    setLoadingSpots(true);
    try {
      const r = await api.get('/training/learning-spots', { params: { category_id: categoryId } });
      setSpots(r.data.data || []);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load sub-categories');
    } finally {
      setLoadingSpots(false);
    }
  }, [categoryId]);

  useEffect(() => {
    loadSpots();
  }, [loadSpots]);

  // Deep-link from Categories tab: select category / sub-category for course design
  useEffect(() => {
    if (!focusCategoryId) return;
    if (categories.some((c) => c.id === focusCategoryId)) {
      setCategoryId(focusCategoryId);
    }
  }, [focusCategoryId, categories]);

  useEffect(() => {
    if (!focusSpotId || !spots.length) return;
    const s = spots.find((x) => x.id === focusSpotId);
    if (!s) return;
    setSelectedSpot(s);
    loadTitles(s);
    setDetailTitle(null);
    setExpandedTitleId(null);
  }, [focusSpotId, spots]);

  const loadScopedExamsForOutline = async (cat, titleId, spotId) => {
    if (!cat || !titleId || !spotId) {
      setExamList([]);
      return;
    }
    try {
      const r = await api.get('/training/exams', {
        params: {
          category_id: cat,
          training_title_id: titleId,
          learning_spot_id: spotId,
          for_course_outline: true
        }
      });
      setExamList(r.data.data || []);
    } catch {
      setExamList([]);
    }
  };

  const loadTitles = async (spot) => {
    if (!spot) {
      setTitles([]);
      setSelectedCourseGroupId('');
      return;
    }
    setLoadingTitles(true);
    try {
      const r = await api.get(`/training/learning-spots/${spot.id}/titles`);
      const list = r.data.data || [];
      setTitles(list);
      const groups = groupTitlesByVersionGroup(list);
      setSelectedCourseGroupId((prev) => {
        if (prev && groups.some((g) => g.groupId === prev)) return prev;
        return groups[0]?.groupId || '';
      });
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load titles');
    } finally {
      setLoadingTitles(false);
    }
  };

  const selectEdition = (t) => {
    if (!t?.id) return;
    setSelectedCourseGroupId(t.version_group_id || t.id);
    setExpandedTitleId(t.id);
    setCourseWorkspaceTab('details');
    loadTitleDetail(t.id);
  };

  const openEditTitle = (t) => {
    if (t?.status === 'published') {
      setPublishedEditLock({ open: true, title: t });
      return;
    }
    setEditingTitleId(t.id);
    setTitleForm(titleFormFromRecord(t));
    setTitleDialog(true);
  };

  const unpublishCourse = async (titleOrId) => {
    const id = typeof titleOrId === 'string' ? titleOrId : titleOrId?.id;
    if (!id) return;
    try {
      await api.put(`/training/training-titles/${id}`, { status: 'ready_to_preview' });
      toast.success('Course unpublished — you can edit it now. Create a new edition if you prefer to keep the published catalog version.');
      setPublishedEditLock({ open: false, title: null });
      if (selectedSpot) await loadTitles(selectedSpot);
      if (detailTitle?.id === id) await loadTitleDetail(id);
      const t = typeof titleOrId === 'object' ? { ...titleOrId, status: 'ready_to_preview' } : null;
      if (t?.id) {
        setEditingTitleId(t.id);
        setTitleForm(titleFormFromRecord(t));
        setTitleDialog(true);
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to unpublish');
    }
  };

  /** Placeholder / empty rows get first content in place; further items attach to same slot. */
  const rowAcceptsInPlaceAttach = (anchor, items = detailTitle?.contentItems) => {
    return isEmptyPlaceholderSlot(anchor, items);
  };

  const resolveOutlineActionTarget = (rowIndex) => {
    if (rowIndex == null || !detailTitle?.contentItems) {
      return { insertAfterIndex: rowIndex, attachItemId: null, slotAnchorId: null };
    }
    const anchors = outlineAnchors(detailTitle.contentItems);
    const anchor = anchors[rowIndex];
    if (!anchor) {
      return { insertAfterIndex: rowIndex, attachItemId: null, slotAnchorId: null };
    }
    const attachInPlace = rowAcceptsInPlaceAttach(anchor, detailTitle.contentItems);
    return {
      insertAfterIndex: rowIndex,
      attachItemId: attachInPlace ? anchor.id : null,
      slotAnchorId: anchor.id
    };
  };

  const toggleSlotExpanded = (anchorId) => {
    setExpandedSlotIds((prev) => ({ ...prev, [anchorId]: !prev[anchorId] }));
  };

  const removeOutlineAttachment = async (it) => {
    if (!detailTitle || !it?.id) return;
    if (detailTitle.status === 'published') {
      setPublishedEditLock({ open: true, title: detailTitle });
      return;
    }
    if (!window.confirm('Remove this attachment from the outline slot?')) return;
    try {
      await api.delete(`/training/training-titles/${detailTitle.id}/content-items/${it.id}`);
      toast.success('Attachment removed');
      loadTitleDetail(detailTitle.id);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to remove attachment');
    }
  };

  const clearOutlineAttachment = async (it) => {
    if (!detailTitle || !it?.id) return;
    if (detailTitle.status === 'published') {
      setPublishedEditLock({ open: true, title: detailTitle });
      return;
    }
    if (!window.confirm('Remove the attached content from this row? The outline slot will stay as a placeholder.')) return;
    try {
      await api.put(`/training/training-titles/${detailTitle.id}/content-items/${it.id}`, {
        item_type: 'placeholder',
        is_placeholder: true,
        material_id: null,
        exam_id: null,
        content_url: null,
        content_type: null,
        label: 'Add content',
        break_duration_value: null,
        break_duration_unit: null
      });
      toast.success('Attachment cleared');
      loadTitleDetail(detailTitle.id);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to clear attachment');
    }
  };

  const openCoursePreview = async (titleId) => {
    const id = titleId || detailTitle?.id || expandedTitleId;
    if (!id) return;
    try {
      if (!detailTitle || detailTitle.id !== id) {
        await loadTitleDetail(id);
      }
      setPreviewOpen(true);
    } catch (e) {
      toast.error('Could not open preview');
    }
  };

  const renderEditionActions = (t) => {
    const isSelected = expandedTitleId === t.id;
    return (
        <Stack direction="row" alignItems="center" spacing={0.5} flexWrap="wrap" onClick={(e) => e.stopPropagation()} useFlexGap>
        <Tooltip title={t.status === 'published' ? 'Published — cannot edit (unpublish first)' : 'Edit course'}>
          <IconButton size="small" aria-label="Edit course" onClick={() => openEditTitle(t)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Button
          size="small"
          variant="outlined"
          color="primary"
          startIcon={<NewEditionIcon />}
          disabled={(t.edition_number || 0) >= 9999}
          onClick={() => publishNewEdition(t.id)}
          sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
        >
          New edition
        </Button>
        <Tooltip title="Course outline">
          <IconButton
            size="small"
            color={isSelected && courseWorkspaceTab === 'outline' ? 'secondary' : 'default'}
            aria-label="Course outline"
            onClick={() => goDesignOutline(t.id)}
          >
            <OutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {(t.status === 'ready_to_preview' || t.status === 'published' || t.status === 'draft') && (
          <Tooltip title="Preview">
            <IconButton size="small" aria-label="Preview" onClick={() => openCoursePreview(t.id)}>
              <ViewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="Delete">
          <IconButton size="small" color="error" aria-label="Delete" onClick={() => deleteTitle(t.id)}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    );
  };

  const renderEditionRow = (t) => {
    const isSelected = expandedTitleId === t.id;
    const edLabel = formatCourseEdition(t.edition_number) || 'Draft (not published)';
    return (
      <Paper
        key={t.id}
        variant="outlined"
        onClick={() => selectEdition(t)}
        sx={{
          p: 1.25,
          cursor: 'pointer',
          bgcolor: isSelected ? '#FFF4D6' : '#F7FBF8',
          borderColor: isSelected ? '#E6A817' : '#D0E8D9',
          borderWidth: isSelected ? 2 : 1,
          transition: 'background-color 0.15s, border-color 0.15s'
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" useFlexGap>
          <Typography fontWeight={600} sx={{ fontFamily: 'monospace' }}>
            {edLabel}
          </Typography>
          {t.course_code && (
            <Chip size="small" variant="outlined" label={t.course_code} sx={{ fontFamily: 'monospace' }} />
          )}
          <Chip size="small" label={STATUS_LABELS[t.status] || t.status} variant="outlined" />
          {t.delivery_type === 'ILT' && t.schedule_model === 'weekly' && (
            <Chip size="small" variant="outlined" label={formatWeeklyDaysLabel(t) || 'Set days'} />
          )}
          <Box sx={{ flex: 1 }} />
          {renderEditionActions(t)}
        </Stack>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.75 }}>
          Owner: {t.owner ? `${t.owner.first_name} ${t.owner.last_name}` : '—'} · Instructors:{' '}
          {(t.instructorLinks || []).map((l) => l.instructor?.email).filter(Boolean).join(', ') || '—'}
        </Typography>
        {loadingDetail && detailTitle?.id === t.id ? <CircularProgress sx={{ mt: 1 }} size={20} /> : null}
      </Paper>
    );
  };

  const loadTitleDetail = async (titleId) => {
    setLoadingDetail(true);
    try {
      const r = await api.get(`/training/training-titles/${titleId}`);
      const data = r.data.data;
      setDetailTitle(data);
      setOutlineFormatTab(data?.outline_format || 'sequence');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to load title detail');
    } finally {
      setLoadingDetail(false);
    }
  };

  const changeOutlineFormat = async (nextFormat) => {
    if (!detailTitle?.id) {
      setOutlineFormatTab(nextFormat);
      return;
    }
    setOutlineFormatTab(nextFormat);
    if (detailTitle.outline_format === nextFormat) return;
    try {
      await api.put(`/training/training-titles/${detailTitle.id}`, { outline_format: nextFormat });
      await loadTitleDetail(detailTitle.id);
      toast.success(`Outline format: ${OUTLINE_FORMAT_TABS.find((x) => x.value === nextFormat)?.label || nextFormat}`);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to update outline format');
      setOutlineFormatTab(detailTitle.outline_format || 'sequence');
    }
  };

  const schedulePayloadForInsert = () => {
    const tab = OUTLINE_FORMAT_TABS.find((x) => x.value === outlineFormatTab);
    if (!tab?.slotNoun) return {};
    const n = parseInt(scheduleSlotDraft, 10);
    if (!Number.isFinite(n) || n < 1) return {};
    return {
      schedule_slot: n,
      schedule_label: `${tab.slotNoun} ${n}`
    };
  };

  const goDesignOutline = async (titleId) => {
    setExpandedTitleId(titleId);
    setCourseWorkspaceTab('outline');
    await loadTitleDetail(titleId);
    window.setTimeout(() => {
      document.getElementById('training-course-outline')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 150);
  };

  const saveSpot = async () => {
    if (!spotForm.name?.trim()) {
      toast.error('Sub-category name is required');
      return;
    }
    try {
      if (editingSpotId) {
        await api.put(`/training/learning-spots/${editingSpotId}`, {
          name: spotForm.name,
          description: spotForm.description,
          sort_order: spotForm.sort_order
        });
      } else {
        await api.post('/training/learning-spots', {
          category_id: categoryId,
          name: spotForm.name,
          description: spotForm.description,
          sort_order: spotForm.sort_order
        });
      }
      toast.success('Sub-category saved');
      setSpotDialog(false);
      setEditingSpotId(null);
      setSpotForm({ name: '', description: '', sort_order: 0 });
      loadSpots();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Save failed');
    }
  };

  const deleteSpot = async (id) => {
    if (!window.confirm('Delete this sub-category and all courses under it?')) return;
    try {
      await api.delete(`/training/learning-spots/${id}`);
      toast.success('Deleted');
      if (selectedSpot?.id === id) {
        setSelectedSpot(null);
        setTitles([]);
      }
      loadSpots();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Delete failed');
    }
  };

  const saveTitle = async () => {
    if (!titleForm.name?.trim()) {
      toast.error('Course name is required');
      return;
    }
    if (!selectedSpot) return;
    const sm = titleForm.delivery_type === 'SPT' ? 'none' : titleForm.schedule_model || 'weekly';
    if (titleForm.delivery_type === 'ILT' && sm === 'weekly' && (!titleForm.weekdays || titleForm.weekdays.length === 0)) {
      toast.error('For weekly ILT, select at least one day (Mon–Sun).');
      return;
    }
    const payload = {
      name: titleForm.name,
      delivery_type: titleForm.delivery_type,
      start_date: titleForm.start_date || null,
      end_date: titleForm.end_date || null,
      schedule_model: sm,
      weekdays: titleForm.delivery_type === 'ILT' && sm === 'weekly' ? titleForm.weekdays : null,
      session_start_time: titleForm.session_start_time || null,
      session_end_time: titleForm.session_end_time || null,
      owner_user_id: titleForm.owner_user_id || undefined,
      instructor_user_ids: titleForm.instructor_user_ids || [],
      status: titleForm.status,
      sort_order: titleForm.sort_order != null && titleForm.sort_order !== '' ? parseInt(titleForm.sort_order, 10) || 0 : 0,
      outline_format: titleForm.outline_format || 'sequence',
      allow_drop_from_enrollment: !!titleForm.allow_drop_from_enrollment,
      drop_window_days: titleForm.allow_drop_from_enrollment
        ? Math.max(1, parseInt(titleForm.drop_window_days, 10) || 14)
        : 14,
      max_enroll_drop_cycles: Math.max(1, parseInt(titleForm.max_enroll_drop_cycles, 10) || 1)
    };
    try {
      const prevEditId = editingTitleId;
      if (editingTitleId) {
        await api.put(`/training/training-titles/${editingTitleId}`, payload);
        toast.success('Course updated');
      } else {
        await api.post(`/training/learning-spots/${selectedSpot.id}/titles`, payload);
        toast.success('Course created with 10 outline placeholders');
      }
      setTitleDialog(false);
      setEditingTitleId(null);
      setTitleForm(emptyTitleForm());
      loadTitles(selectedSpot);
      if (prevEditId && detailTitle?.id === prevEditId) {
        loadTitleDetail(prevEditId);
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Save failed');
    }
  };

  const deleteTitle = async (id) => {
    if (!window.confirm('Delete this course?')) return;
    try {
      await api.delete(`/training/training-titles/${id}`);
      toast.success('Deleted');
      loadTitles(selectedSpot);
      if (detailTitle?.id === id) {
        setDetailTitle(null);
        setExpandedTitleId(null);
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Delete failed');
    }
  };

  const openBreakDialog = (rowIndex) => {
    const { insertAfterIndex, attachItemId, slotAnchorId: anchorId } = resolveOutlineActionTarget(rowIndex);
    setBreakInsertAfter(insertAfterIndex);
    setAttachTargetItemId(attachItemId);
    setSlotAnchorId(anchorId);
    setBreakDialog(true);
  };

  const openExamDialog = async (rowIndex) => {
    const { insertAfterIndex, attachItemId, slotAnchorId: anchorId } = resolveOutlineActionTarget(rowIndex);
    setExamInsertAfter(insertAfterIndex);
    setAttachTargetItemId(attachItemId);
    setSlotAnchorId(anchorId);
    setSelectedExamId('');
    setExamDialog(true);
    await loadScopedExamsForOutline(categoryId, detailTitle?.id, selectedSpot?.id);
  };

  const addExamToOutline = async () => {
    if (!detailTitle || !selectedExamId) {
      toast.error('Select an exam');
      return;
    }
    try {
      if (attachTargetItemId) {
        await api.put(`/training/training-titles/${detailTitle.id}/content-items/${attachTargetItemId}`, {
          item_type: 'exam',
          is_placeholder: false,
          exam_id: selectedExamId,
          material_id: null
        });
        toast.success('Exam attached to this outline row');
      } else if (slotAnchorId) {
        await api.post(`/training/training-titles/${detailTitle.id}/content-items`, {
          item_type: 'exam',
          exam_id: selectedExamId,
          parent_item_id: slotAnchorId,
          ...schedulePayloadForInsert()
        });
        toast.success('Exam added to this outline slot');
        setExpandedSlotIds((prev) => ({ ...prev, [slotAnchorId]: true }));
      } else {
        const rowRes = await api.post(`/training/training-titles/${detailTitle.id}/content-items`, {
          item_type: 'exam',
          exam_id: selectedExamId,
          ...schedulePayloadForInsert()
        });
        const newItemId = rowRes.data.data.id;
        await reorderAfterInsert(detailTitle.id, newItemId, examInsertAfter);
        toast.success('Exam added to outline');
      }
      setExamDialog(false);
      setExamInsertAfter(null);
      setAttachTargetItemId(null);
      setSlotAnchorId(null);
      loadTitleDetail(detailTitle.id);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to add exam');
    }
  };

  const addBreak = async () => {
    if (!detailTitle) return;
    try {
      if (attachTargetItemId) {
        await api.put(`/training/training-titles/${detailTitle.id}/content-items/${attachTargetItemId}`, {
          item_type: 'break',
          is_placeholder: false,
          break_duration_value: Number(breakForm.duration_value),
          break_duration_unit: breakForm.duration_unit,
          label: breakForm.label,
          material_id: null,
          exam_id: null
        });
        toast.success('Break attached to this outline row');
      } else if (slotAnchorId) {
        await api.post(`/training/training-titles/${detailTitle.id}/content-items`, {
          item_type: 'break',
          break_duration_value: Number(breakForm.duration_value),
          break_duration_unit: breakForm.duration_unit,
          label: breakForm.label,
          parent_item_id: slotAnchorId,
          ...schedulePayloadForInsert()
        });
        toast.success('Break added to this outline slot');
        setExpandedSlotIds((prev) => ({ ...prev, [slotAnchorId]: true }));
      } else {
        const rowRes = await api.post(`/training/training-titles/${detailTitle.id}/content-items`, {
          item_type: 'break',
          break_duration_value: Number(breakForm.duration_value),
          break_duration_unit: breakForm.duration_unit,
          label: breakForm.label,
          ...schedulePayloadForInsert()
        });
        const newItemId = rowRes.data.data.id;
        await reorderAfterInsert(detailTitle.id, newItemId, breakInsertAfter);
        toast.success('Break added');
      }
      setBreakDialog(false);
      setBreakInsertAfter(null);
      setAttachTargetItemId(null);
      setSlotAnchorId(null);
      setBreakForm({ duration_value: 1, duration_unit: 'days', label: 'Break' });
      loadTitleDetail(detailTitle.id);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    }
  };

  const addLiveVideoCall = async (rowIndex = null) => {
    if (!detailTitle) return;
    try {
      const { insertAfterIndex, attachItemId, slotAnchorId: anchorId } = resolveOutlineActionTarget(rowIndex);
      if (attachItemId) {
        await api.put(`/training/training-titles/${detailTitle.id}/content-items/${attachItemId}`, {
          item_type: 'live_video_call',
          is_placeholder: false,
          label: 'Join Video Call',
          notes: 'Learners join the instructor-launched live session (video + audio).',
          content_type: 'live_video_call',
          material_id: null,
          exam_id: null
        });
        toast.success('Join Video Call attached to this outline row');
        loadTitleDetail(detailTitle.id);
      } else if (anchorId) {
        await api.post(`/training/training-titles/${detailTitle.id}/content-items`, {
          item_type: 'live_video_call',
          label: 'Join Video Call',
          notes: 'Learners join the instructor-launched live session (video + audio).',
          parent_item_id: anchorId,
          ...schedulePayloadForInsert()
        });
        toast.success('Join Video Call added to this outline slot');
        setExpandedSlotIds((prev) => ({ ...prev, [anchorId]: true }));
        loadTitleDetail(detailTitle.id);
      } else {
        const rowRes = await api.post(`/training/training-titles/${detailTitle.id}/content-items`, {
          item_type: 'live_video_call',
          label: 'Join Video Call',
          notes: 'Learners join the instructor-launched live session (video + audio).',
          ...schedulePayloadForInsert()
        });
        const newItemId = rowRes.data.data.id;
        await reorderAfterInsert(detailTitle.id, newItemId, insertAfterIndex);
        toast.success('Join Video Call added to outline');
        loadTitleDetail(detailTitle.id);
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to add Join Video Call');
    }
  };

  const reorderAfterInsert = async (titleId, newItemId, insertAfterIndex) => {
    const r = await api.get(`/training/training-titles/${titleId}`);
    const items = r.data.data.contentItems || [];
    const ids = items.map((x) => x.id);
    const filtered = ids.filter((id) => id !== newItemId);
    const pos = insertAfterIndex == null ? filtered.length : Math.min(insertAfterIndex + 1, filtered.length);
    const ordered = [...filtered.slice(0, pos), newItemId, ...filtered.slice(pos)];
    await api.put(`/training/training-titles/${titleId}/content-items/reorder`, { ordered_ids: ordered });
    await loadTitleDetail(titleId);
  };

  const setCourseStatus = async (status) => {
    if (!detailTitle) return;
    try {
      await api.put(`/training/training-titles/${detailTitle.id}`, { status });
      toast.success(
        status === 'ready_to_preview'
          ? 'Course saved — ready to preview (not in catalog yet)'
          : status === 'published'
            ? 'Course published — visible in Browse Catalog (edition V0001 on first publish)'
            : 'Updated'
      );
      await loadTitleDetail(detailTitle.id);
      if (selectedSpot) loadTitles(selectedSpot);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    }
  };

  const publishNewEdition = async (fromTitleId) => {
    const sourceId = fromTitleId || detailTitle?.id;
    if (!sourceId) return;
    if (
      !window.confirm(
        'Create a new edition as a draft copy of this course (outline + instructors)? Example: V0001 → V0002. You can create as many editions as needed — any edition can be published.'
      )
    ) {
      return;
    }
    try {
      const r = await api.post(`/training/training-titles/${sourceId}/publish-new-edition`);
      toast.success(r.data?.message || 'New edition created');
      const newId = r.data?.data?.id;
      await loadTitles(selectedSpot);
      if (newId) {
        setExpandedTitleId(newId);
        await loadTitleDetail(newId);
      } else if (detailTitle?.id) {
        await loadTitleDetail(detailTitle.id);
      }
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    }
  };

  const saveInlineMaterial = async () => {
    if (!detailTitle || !categoryId || !selectedSpot) return;
    if (!matForm.title?.trim()) {
      toast.error('Title is required');
      return;
    }
    const ct = matForm.content_type;
    if (ct === 'video') {
      if (!matFile && !matForm.content_url?.trim()) {
        toast.error('Upload a video file or paste a video URL');
        return;
      }
    } else if (CONTENT_TYPES_NEEDING_URL.has(ct) && !matForm.content_url?.trim()) {
      toast.error('Paste a URL or link for this content type');
      return;
    }
    try {
      let content_url = matForm.content_url || '';
      let file_path = null;
      if (ct === 'video' && matFile) {
        const fd = new FormData();
        fd.append('file', matFile);
        const up = await api.post('/training/materials/upload', fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        file_path = up.data.data.file_path;
        content_url = up.data.data.public_url || file_path || content_url;
      }
      const payload = {
        title: matForm.title,
        description: matForm.description || null,
        category_id: categoryId,
        content_type: matForm.content_type,
        content_url: ct === 'video' && !matForm.content_url?.trim() ? content_url : matForm.content_url || null,
        file_path: ct === 'video' ? file_path : null,
        duration_minutes: matForm.duration_minutes ? parseInt(matForm.duration_minutes, 10) : null,
        difficulty_level: matForm.difficulty_level,
        training_title_id: detailTitle.id,
        learning_spot_id: selectedSpot.id
      };
      const matRes = await api.post('/training/materials', payload);
      const materialId = matRes.data.data.id;
      if (attachTargetItemId) {
        await api.put(`/training/training-titles/${detailTitle.id}/content-items/${attachTargetItemId}`, {
          item_type: 'material',
          is_placeholder: false,
          material_id: materialId,
          exam_id: null,
          label: matForm.title
        });
        toast.success('Material attached to this outline row');
      } else if (slotAnchorId) {
        await api.post(`/training/training-titles/${detailTitle.id}/content-items`, {
          item_type: 'material',
          material_id: materialId,
          parent_item_id: slotAnchorId,
          ...schedulePayloadForInsert()
        });
        toast.success('Material added to this outline slot');
        setExpandedSlotIds((prev) => ({ ...prev, [slotAnchorId]: true }));
      } else {
        const rowRes = await api.post(`/training/training-titles/${detailTitle.id}/content-items`, {
          item_type: 'material',
          material_id: materialId,
          ...schedulePayloadForInsert()
        });
        const newItemId = rowRes.data.data.id;
        await reorderAfterInsert(detailTitle.id, newItemId, inlineInsertAfter);
        toast.success('Added to outline and Materials library');
      }
      onMaterialsRefresh?.();
      setInlineMatOpen(false);
      setMatForm({
        title: '',
        description: '',
        content_type: 'youtube',
        content_url: '',
        duration_minutes: '',
        difficulty_level: 'beginner'
      });
      setMatFile(null);
      setInlineInsertAfter(null);
      setAttachTargetItemId(null);
      setSlotAnchorId(null);
      loadTitleDetail(detailTitle.id);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    }
  };

  const linkMaterial = async () => {
    if (!detailTitle || !linkMaterialId) return;
    try {
      if (attachTargetItemId) {
        await api.put(`/training/training-titles/${detailTitle.id}/content-items/${attachTargetItemId}`, {
          item_type: 'material',
          is_placeholder: false,
          material_id: linkMaterialId,
          exam_id: null
        });
        toast.success('Material attached to this outline row');
      } else if (slotAnchorId) {
        await api.post(`/training/training-titles/${detailTitle.id}/content-items`, {
          item_type: 'material',
          material_id: linkMaterialId,
          parent_item_id: slotAnchorId,
          ...schedulePayloadForInsert()
        });
        toast.success('Material added to this outline slot');
        setExpandedSlotIds((prev) => ({ ...prev, [slotAnchorId]: true }));
      } else {
        const rowRes = await api.post(`/training/training-titles/${detailTitle.id}/content-items`, {
          item_type: 'material',
          material_id: linkMaterialId,
          ...schedulePayloadForInsert()
        });
        const newItemId = rowRes.data.data.id;
        await reorderAfterInsert(detailTitle.id, newItemId, linkInsertAfter);
        toast.success('Material linked');
      }
      setLinkMatDialog(false);
      setLinkMaterialId('');
      setLinkInsertAfter(null);
      setAttachTargetItemId(null);
      setSlotAnchorId(null);
      onMaterialsRefresh?.();
      loadTitleDetail(detailTitle.id);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    }
  };

  const viewOutlineItem = (it) => {
    if (it.item_type === 'exam') {
      toast.info('Exams are configured under Training → Exams. Learner-facing exam player can be added later.');
      return;
    }
    if (it.item_type === 'live_video_call') {
      toast.info('Instructors launch this from Instructor Dashboard; learners join from My Training when live.');
      return;
    }
    if (it.item_type === 'placeholder') {
      toast.info('Empty outline slot. Use Actions → Attach to put material, Join Video Call, exam, or break on this row.');
      return;
    }
    if (it.item_type === 'material' && it.material) {
      const u = it.material.content_url || it.material.file_path;
      if (!u) {
        toast.info('No link for this item');
        return;
      }
      const abs = u.startsWith('http') ? u : `${window.location.origin}${u.startsWith('/') ? '' : '/'}${u}`;
      window.open(abs, '_blank', 'noopener,noreferrer');
    } else if (it.content_url) {
      window.open(it.content_url, '_blank', 'noopener,noreferrer');
    } else {
      toast.info('Nothing to open');
    }
  };

  const openAddContent = (rowIndex) => {
    const { insertAfterIndex, attachItemId, slotAnchorId: anchorId } = resolveOutlineActionTarget(rowIndex);
    setInlineInsertAfter(insertAfterIndex);
    setAttachTargetItemId(attachItemId);
    setSlotAnchorId(anchorId);
    setMatForm({
      title: '',
      description: '',
      content_type: 'youtube',
      content_url: '',
      duration_minutes: '',
      difficulty_level: 'beginner'
    });
    setMatFile(null);
    setInlineMatOpen(true);
  };

  const openAddMenu = (event, rowIndex) => {
    setAddMenuAnchor(event.currentTarget);
    setAddMenuAfterIdx(rowIndex);
  };

  const closeAddMenu = () => {
    setAddMenuAnchor(null);
  };

  const openLinkExisting = (rowIndex) => {
    const { insertAfterIndex, attachItemId, slotAnchorId: anchorId } = resolveOutlineActionTarget(rowIndex);
    setLinkInsertAfter(insertAfterIndex);
    setAttachTargetItemId(attachItemId);
    setSlotAnchorId(anchorId);
    setLinkMaterialId('');
    setLinkMatDialog(true);
  };

  /** Insert a new placeholder row after the selected outline row (or at end). */
  const addPlaceholderAfter = async (insertAfterIndex = null) => {
    if (!detailTitle) return;
    try {
      const rowRes = await api.post(`/training/training-titles/${detailTitle.id}/content-items`, { item_type: 'placeholder' });
      const newItemId = rowRes.data.data.id;
      await reorderAfterInsert(detailTitle.id, newItemId, insertAfterIndex);
      toast.success('Placeholder added');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    }
  };

  const addPlaceholder = async () => {
    await addPlaceholderAfter(null);
  };

  const removeContentItem = async (itemId) => {
    if (!detailTitle || !window.confirm('Remove this row?')) return;
    try {
      await api.delete(`/training/training-titles/${detailTitle.id}/content-items/${itemId}`);
      loadTitleDetail(detailTitle.id);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    }
  };

  const onDragStartRow = (e, idx) => {
    e.dataTransfer.setData('text/plain', String(idx));
    e.dataTransfer.effectAllowed = 'move';
    setDragRowIdx(idx);
  };

  const onDragOverRow = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const onDropRow = async (e, dropIdx) => {
    e.preventDefault();
    if (!detailTitle) return;
    const from = parseInt(e.dataTransfer.getData('text/plain'), 10);
    setDragRowIdx(null);
    if (Number.isNaN(from) || from === dropIdx) return;
    const items = outlineAnchors(detailTitle.contentItems);
    const [row] = items.splice(from, 1);
    items.splice(dropIdx, 0, row);
    const ordered_ids = items.map((x) => x.id);
    try {
      await api.put(`/training/training-titles/${detailTitle.id}/content-items/reorder`, { ordered_ids });
      loadTitleDetail(detailTitle.id);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Reorder failed');
    }
  };

  const saveEditOutlineItem = async () => {
    if (!detailTitle || !editOutlineItem) return;
    try {
      await api.put(`/training/training-titles/${detailTitle.id}/content-items/${editOutlineItem.id}`, {
        label: editOutlineLabel
      });
      toast.success('Updated');
      setEditOutlineItem(null);
      loadTitleDetail(detailTitle.id);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed');
    }
  };

  const matsForCategory = materials.filter((m) => m.category_id === categoryId);
  const usedMaterialIds = collectOutlineMaterialIds(detailTitle?.contentItems);
  const linkableMaterials = matsForCategory.filter((m) => !usedMaterialIds.has(m.id));
  const usedExamIds = collectOutlineExamIds(detailTitle?.contentItems);
  const linkableExams = examList.filter((ex) => !usedExamIds.has(ex.id));

  const catName = categories.find((c) => c.id === categoryId)?.name;

  const outlineTable =
    detailTitle &&
    !loadingDetail &&
    (() => {
      const allItems = detailTitle.contentItems || [];
      const anchors = outlineAnchors(allItems);
      const typeLabel = (it) => {
        if (it.item_type === 'break') return 'Break';
        if (it.item_type === 'exam') return 'Exam';
        if (it.item_type === 'live_video_call') return 'Join Video Call';
        if (it.item_type === 'placeholder') return 'Placeholder';
        if (it.item_type === 'material' && it.material?.content_type) {
          return CONTENT_TYPE_LABELS[it.material.content_type] || it.material.content_type;
        }
        if (it.content_type) return CONTENT_TYPE_LABELS[it.content_type] || it.content_type;
        return it.item_type;
      };
      const summary = (it) => {
        if (it.item_type === 'break') return `${it.label || 'Break'} · ${it.break_duration_value} ${it.break_duration_unit}`;
        if (it.item_type === 'exam') return it.exam?.name || it.label || 'Exam';
        if (it.item_type === 'live_video_call') {
          return it.label || 'Join Video Call — instructor launches; learners join from My Training';
        }
        if (it.item_type === 'material') {
          const code = it.material?.material_code ? ` · ${it.material.material_code}` : '';
          return `${it.material?.title || it.label || ''}${code}`;
        }
        if (it.item_type === 'placeholder') {
          return it.label || 'Empty slot — use Actions to attach content to this row';
        }
        return it.label || it.content_url || '—';
      };
      return (
        <>
          <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1} sx={{ mb: 2 }}>
            <Chip size="small" label={STATUS_LABELS[detailTitle.status] || detailTitle.status} color={detailTitle.status === 'published' ? 'success' : 'default'} />
            {formatCourseEdition(detailTitle.edition_number) && (
              <Chip size="small" label={formatCourseEdition(detailTitle.edition_number)} variant="outlined" color="primary" />
            )}
            {detailTitle.status === 'draft' && (
              <Button size="small" variant="contained" onClick={() => setCourseStatus('ready_to_preview')}>
                Save course (ready to preview)
              </Button>
            )}
            {(detailTitle.status === 'draft' || detailTitle.status === 'ready_to_preview') && (
              <Button size="small" variant="contained" color="success" onClick={() => setCourseStatus('published')}>
                Publish
              </Button>
            )}
            {detailTitle.status === 'published' && (
              <Alert
                severity="warning"
                sx={{ py: 0.5, flex: 1, minWidth: 280 }}
                action={
                  <Stack direction="row" spacing={1}>
                    <Button color="inherit" size="small" onClick={() => unpublishCourse(detailTitle)}>
                      Unpublish
                    </Button>
                    <Button
                      color="inherit"
                      size="small"
                      startIcon={<NewEditionIcon />}
                      disabled={(detailTitle.edition_number || 0) >= 9999}
                      onClick={() => publishNewEdition(detailTitle.id)}
                    >
                      New edition
                    </Button>
                  </Stack>
                }
              >
                Published — editing is locked. Unpublish to change this edition, or create a new edition (instructor can
                publish another edition anytime).
              </Alert>
            )}
            {(detailTitle.edition_number || 0) < 9999 && (
              <Button
                size="small"
                variant="outlined"
                color="primary"
                startIcon={<NewEditionIcon />}
                onClick={() => publishNewEdition(detailTitle.id)}
              >
                Create new edition
              </Button>
            )}
          </Stack>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={36} />
                  <TableCell width={40} />
                  <TableCell width={56}>
                    <Tooltip title="Sequence number — outline order only (not from/to calendar dates)">
                      <span>#</span>
                    </Tooltip>
                  </TableCell>
                  {outlineFormatTab !== 'sequence' && (
                    <TableCell width={100}>
                      {OUTLINE_FORMAT_TABS.find((x) => x.value === outlineFormatTab)?.slotNoun || 'Slot'}
                    </TableCell>
                  )}
                  <TableCell>Type</TableCell>
                  <TableCell>Summary</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap', minWidth: 280 }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {anchors.map((anchor, idx) => {
                  const slotItems = getSlotAttachments(allItems, anchor);
                  const multi = slotItems.length > 1;
                  const expanded = !!expandedSlotIds[anchor.id];
                  const primary = slotItems[0] || anchor;
                  const collapsedType = multi ? `Multiple (${slotItems.length})` : typeLabel(primary);
                  const collapsedSummary = multi
                    ? `${summary(slotItems[0])}${slotItems.length > 1 ? ` · +${slotItems.length - 1} more` : ''}`
                    : summary(primary);
                  return (
                  <TableRow
                    key={anchor.id}
                    hover
                    onDragOver={onDragOverRow}
                    onDrop={(e) => onDropRow(e, idx)}
                    sx={{ opacity: dragRowIdx === idx ? 0.7 : 1 }}
                  >
                    <TableCell>
                      {multi ? (
                        <Tooltip title={expanded ? 'Collapse attachments' : 'Show all attachments in this slot'}>
                          <IconButton
                            size="small"
                            onClick={() => toggleSlotExpanded(anchor.id)}
                            aria-label={expanded ? 'Collapse slot' : 'Expand slot'}
                            sx={{ border: '1px solid',
                              borderColor: 'divider',
                              width: 28,
                              height: 28 }}
                          >
                            {expanded ? (
                              <CollapseSlotIcon sx={{ fontSize: 18 }} />
                            ) : (
                              <AddIcon sx={{ fontSize: 18 }} />
                            )}
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <Box
                        component="span"
                        draggable
                        onDragStart={(e) => onDragStartRow(e, idx)}
                        sx={{ cursor: 'grab', display: 'inline-flex', alignItems: 'center', p: 0.5 }}
                        aria-label="Drag to reorder"
                      >
                        <DragIcon fontSize="small" color="action" />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} component="span">
                        {idx + 1}
                      </Typography>
                    </TableCell>
                    {outlineFormatTab !== 'sequence' && (
                      <TableCell>
                        {anchor.schedule_label ||
                          (anchor.schedule_slot != null
                            ? `${OUTLINE_FORMAT_TABS.find((x) => x.value === outlineFormatTab)?.slotNoun || 'Slot'} ${anchor.schedule_slot}`
                            : '—')}
                      </TableCell>
                    )}
                    <TableCell>
                      {expanded && multi ? (
                        <Typography variant="caption" color="text.secondary">
                          {slotItems.length} in slot
                        </Typography>
                      ) : (
                        <>
                          <Chip size="small" label={collapsedType} />
                          {primary.material?.material_code && (
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontFamily: 'monospace' }}>
                              {primary.material.material_code}
                            </Typography>
                          )}
                        </>
                      )}
                    </TableCell>
                    <TableCell>
                      {expanded && multi ? (
                        <Stack spacing={0.75}>
                          {slotItems.map((att) => (
                            <Stack key={att.id} direction="row" alignItems="center" spacing={1} useFlexGap>
                              <Chip size="small" variant="outlined" label={typeLabel(att)} />
                              <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }}>
                                {summary(att)}
                              </Typography>
                              {detailTitle.status !== 'published' && (
                                <Tooltip title="Remove this attachment">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => removeOutlineAttachment(att)}
                                    aria-label="Remove attachment"
                                  >
                                    <DeleteIcon sx={{ fontSize: 16 }} />
                                  </IconButton>
                                </Tooltip>
                              )}
                            </Stack>
                          ))}
                        </Stack>
                      ) : (
                        collapsedSummary
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0} justifyContent="flex-end" flexWrap="nowrap" useFlexGap>
                        {detailTitle.status === 'published' ? (
                          <Tooltip title="Published — unpublish to change outline">
                            <span>
                              <IconButton size="small" disabled aria-label="Actions locked">
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : (
                          <>
                        <Tooltip title="Add content to this outline slot (material, video, exam, break…)">
                          <IconButton size="small" color="primary" onClick={(e) => openAddMenu(e, idx)} aria-label="Attach or add content">
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Link library material to this slot">
                          <IconButton size="small" onClick={() => openLinkExisting(idx)} aria-label="Link library">
                            <LibraryIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Add Join Video Call to this slot">
                          <IconButton size="small" color="secondary" onClick={() => addLiveVideoCall(idx)} aria-label="Join Video Call">
                            <VideoCallIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Add break to this slot">
                          <IconButton size="small" onClick={() => openBreakDialog(idx)} aria-label="Break">
                            <BreakIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Add exam to this slot">
                          <IconButton size="small" onClick={() => openExamDialog(idx)} aria-label="Exam">
                            <ExamOutlineIcon fontSize="small" color="secondary" />
                          </IconButton>
                        </Tooltip>
                          </>
                        )}
                        <Tooltip title="View / open first item">
                          <IconButton size="small" onClick={() => viewOutlineItem(primary)} aria-label="View">
                            <ViewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {detailTitle.status !== 'published' && (
                          <>
                        <Tooltip title="Edit slot label">
                          <IconButton
                            size="small"
                            onClick={() => {
                              setEditOutlineItem(anchor);
                              setEditOutlineLabel(anchor.label || anchor.material?.title || '');
                            }}
                            aria-label="Edit label"
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {slotItems.length === 1 && !isEmptyPlaceholderSlot(anchor, allItems) && (
                          <Tooltip title="Remove attachment">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => removeOutlineAttachment(primary)}
                              aria-label="Remove attachment"
                            >
                              <ClearAttachIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Remove entire slot (and all attachments)">
                          <IconButton size="small" color="error" onClick={() => removeContentItem(anchor.id)} aria-label="Remove row">
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                          </>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {detailTitle.status === 'published' ? (
              <Alert severity="info" sx={{ width: '100%' }}>
                Outline is locked while published. Unpublish this edition to change rows, or create a new edition.
              </Alert>
            ) : (
              <>
            <Button variant="outlined" startIcon={<AddIcon />} onClick={(e) => openAddMenu(e, null)}>
              Add content
            </Button>
            <Button variant="outlined" startIcon={<PlaceholderIcon />} onClick={addPlaceholder}>
              Add placeholder
            </Button>
            <Button variant="outlined" startIcon={<VideoCallIcon fontSize="small" />} color="secondary" onClick={() => addLiveVideoCall(null)}>
              Add Join Video Call
            </Button>
            <Button variant="outlined" startIcon={<BreakIcon fontSize="small" />} onClick={() => openBreakDialog(null)}>
              Add break
            </Button>
            <Button variant="outlined" startIcon={<ExamOutlineIcon fontSize="small" />} onClick={() => openExamDialog(null)}>
              Add exam
            </Button>
            <Button variant="outlined" startIcon={<LibraryIcon />} onClick={() => openLinkExisting(null)}>
              Link library
            </Button>
              </>
            )}
            {onOpenMaterialsTab && (
              <Typography variant="caption" color="text.secondary">
                Full library:{' '}
                <Link component="button" type="button" variant="caption" onClick={onOpenMaterialsTab}>
                  Materials tab
                </Link>
              </Typography>
            )}
          </Box>
        </>
      );
    })();

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Curriculum structure
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Example: <strong>Adobe Training</strong> (category) → <strong>Adobe Experience Platform</strong> (sub-category) → courses such as{' '}
        <em>AEP + AJO + CJA</em>, <em>AEM</em>, or <em>Adobe Workfront</em>. Scroll down: pick category, then sub-category, then add each course and its
        outline (lessons, labs, materials).
      </Typography>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%', borderRadius: 2, borderLeft: 4, borderLeftColor: 'primary.main' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <IltIcon color="primary" />
                <Typography variant="subtitle1">Instructor-led (ILT)</Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Live or virtual classes with a schedule (daily, weekly, or other). Set session times and which days class runs. Multiple offerings over time
                can be modeled as separate courses today; dedicated <strong>batches</strong> (e.g. Jan–Mar vs Feb–Apr) are on the roadmap.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card variant="outlined" sx={{ height: '100%', borderRadius: 2, borderLeft: 4, borderLeftColor: 'success.main' }}>
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <SptIcon color="success" />
                <Typography variant="subtitle1">Self-paced (SPT)</Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Videos, documents, and ordered lessons learners complete on their own—no live instructor. Link materials from the Materials tab and order them
                in the course outline. After ILT ends, you can attach recordings the same way.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Alert severity="info" sx={{ mb: 2, borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
          Learner experience (roadmap)
        </Typography>
        <Typography variant="body2" component="div">
          Planned or in progress: <strong>course batches</strong> (cohorts with date ranges), <strong>labs &amp; assignments</strong> tied to courses,
          learner <strong>Q&amp;A</strong>, cohort or course <strong>chat</strong>, and Trainee portal access. Enroll{' '}
          <strong>Trainee</strong> contacts for people who need Training plus Helpdesk without a full employee profile.
        </Typography>
      </Alert>

      {categoryId && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {catName || 'Category'}
          {selectedSpot ? ` → ${selectedSpot.name}` : ''}
          {detailTitle ? ` → ${detailTitle.name}` : ''}
        </Typography>
      )}

      <Card variant="outlined" sx={{ borderRadius: 2, mb: 2 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <CategoryIcon color="primary" />
            <Typography variant="subtitle1">Category</Typography>
          </Stack>
          <FormControl fullWidth sx={{ maxWidth: 520 }}>
            <InputLabel>Training category</InputLabel>
            <Select
              label="Training category"
              value={categoryId}
              onChange={(e) => {
                setCategoryId(e.target.value);
                setSelectedSpot(null);
                setTitles([]);
                setDetailTitle(null);
                setExpandedTitleId(null);
              }}
            >
              {categories.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.short_id ? `[${c.short_id}] ` : ''}{c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {categories.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              No categories yet.{' '}
              {onOpenCategoriesTab ? (
                <Link component="button" type="button" variant="body2" onClick={onOpenCategoriesTab}>
                  Open Categories tab
                </Link>
              ) : (
                'Create one in the Categories tab.'
              )}
            </Typography>
          )}
        </CardContent>
      </Card>

      {categoryId && (
        <Card variant="outlined" sx={SECTION_SUBCAT_SX}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <SubcategoryIcon sx={{ color: '#1B6CA8' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#0F4C75' }}>
                  Sub-categories in “{catName}”
                </Typography>
              </Stack>
              <Button
                size="small"
                variant="contained"
                startIcon={<AddIcon />}
                onClick={() => {
                  setEditingSpotId(null);
                  setSpotForm({ name: '', description: '', sort_order: 0 });
                  setSpotDialog(true);
                }}
              >
                Add sub-category
              </Button>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              Groups related courses (e.g. one row for AEP stack, one for AEM, one for Workfront). Select a row to manage its courses.
            </Typography>
            <FormControl component="fieldset" sx={{ mb: 2 }}>
              <FormLabel component="legend" sx={{ fontSize: '0.85rem', mb: 0.5 }}>
                Courses &amp; editions layout
              </FormLabel>
              <RadioGroup
                row
                value={coursesLayout}
                onChange={(e) => setCoursesLayout(e.target.value)}
              >
                <FormControlLabel
                  value="free"
                  control={<Radio size="small" />}
                  label="Free style (one below the other)"
                />
                <FormControlLabel value="tabbed" control={<Radio size="small" />} label="Tabbed style" />
              </RadioGroup>
            </FormControl>
            {loadingSpots ? (
              <CircularProgress size={28} />
            ) : (
              <List
                dense
                sx={{
                  bgcolor: 'rgba(255,255,255,0.72)',
                  borderRadius: 1.5,
                  border: '1px solid #C5E0F2',
                  overflow: 'hidden'
                }}
              >
                {spots.length === 0 && (
                  <ListItem>
                    <ListItemText
                      primary="No sub-categories yet"
                      secondary="Add one for each product line or audience under this category."
                    />
                  </ListItem>
                )}
                {spots.map((s) => (
                  <ListItem
                    key={s.id}
                    disablePadding
                    secondaryAction={
                      <Box onClick={(e) => e.stopPropagation()}>
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditingSpotId(s.id);
                            setSpotForm({ name: s.name, description: s.description || '', sort_order: s.sort_order });
                            setSpotDialog(true);
                          }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => deleteSpot(s.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    }
                  >
                    <ListItemButton
                      selected={selectedSpot?.id === s.id}
                      onClick={() => {
                        setSelectedSpot(s);
                        loadTitles(s);
                        setDetailTitle(null);
                        setExpandedTitleId(null);
                      }}
                      sx={{
                        '&.Mui-selected': {
                          bgcolor: '#FFF4D6',
                          borderLeft: '4px solid #E6A817',
                          '&:hover': { bgcolor: '#FFEEC0' }
                        }
                      }}
                    >
                      <SubcategoryIcon sx={{ mr: 1, color: selectedSpot?.id === s.id ? '#C48A00' : '#1B6CA8' }} />
                      <ListItemText
                        primary={
                          s.short_id ? (
                            <>
                              <Typography component="span" sx={{ fontFamily: 'monospace', mr: 1 }}>
                                [{s.short_id}]
                              </Typography>
                              {s.name}
                            </>
                          ) : (
                            s.name
                          )
                        }
                        secondary={s.description || ' '}
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      )}

      {selectedSpot && (
        <Card variant="outlined" sx={SECTION_COURSES_SX}>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} flexWrap="wrap" gap={1}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <TitleIcon sx={{ color: '#2E7D4F' }} />
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1B5E3B' }}>
                  Courses in “{selectedSpot.name}”
                </Typography>
              </Stack>
              <Button
                size="small"
                variant="contained"
                color="success"
                startIcon={<AddIcon />}
                onClick={() => {
                  setEditingTitleId(null);
                  setTitleForm(emptyTitleForm());
                  setTitleDialog(true);
                }}
              >
                Add course
              </Button>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Select a course, then pick a version/edition. Use the copy icon to create a new edition from any row (draft or published).
              Layout: <strong>{coursesLayout === 'tabbed' ? 'Tabbed' : 'Free style'}</strong> (change next to Sub-categories).
            </Typography>
            {loadingTitles ? (
              <CircularProgress size={28} />
            ) : groupTitlesByVersionGroup(titles).length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No courses yet. Add a course to start.
              </Typography>
            ) : coursesLayout === 'tabbed' ? (
              (() => {
                const groups = groupTitlesByVersionGroup(titles);
                const activeGroup =
                  groups.find((g) => g.groupId === selectedCourseGroupId) || groups[0];
                const editionTab =
                  activeGroup?.editions.find((e) => e.id === expandedTitleId)?.id ||
                  activeGroup?.editions[activeGroup.editions.length - 1]?.id ||
                  false;
                return (
                  <Box sx={{ bgcolor: 'rgba(255,255,255,0.72)', borderRadius: 1.5, p: 1.5, border: '1px solid #C5E8D1' }}>
                    <Tabs
                      value={activeGroup?.groupId || false}
                      onChange={(_, gid) => {
                        setSelectedCourseGroupId(gid);
                        const g = groups.find((x) => x.groupId === gid);
                        const pick =
                          g?.editions.find((e) => e.id === expandedTitleId) ||
                          g?.editions.find((e) => e.status === 'published') ||
                          g?.editions[g.editions.length - 1];
                        if (pick) selectEdition(pick);
                      }}
                      variant="scrollable"
                      scrollButtons="auto"
                      sx={{ mb: 1, borderBottom: 1, borderColor: 'divider' }}
                    >
                      {groups.map((g) => (
                        <Tab
                          key={g.groupId}
                          value={g.groupId}
                          label={
                            <Stack direction="row" spacing={0.75} alignItems="center">
                              <span>{g.name}</span>
                              {g.course_code ? (
                                <Chip
                                  size="small"
                                  label={g.course_code}
                                  sx={{ height: 18, fontFamily: 'monospace', fontSize: '0.65rem', bgcolor: 'rgba(255,255,255,0.4)' }}
                                />
                              ) : null}
                              <Chip size="small" label={formatEditionVersionLabel(g.editions)} sx={{ height: 20, bgcolor: 'rgba(255,255,255,0.35)' }} />
                            </Stack>
                          }
                          sx={COURSE_TAB_SX.course}
                        />
                      ))}
                    </Tabs>
                    {activeGroup && (
                      <>
                        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ mb: 1 }}>
                          {activeGroup.course_code && (
                            <Chip size="small" variant="outlined" label={activeGroup.course_code} sx={{ fontFamily: 'monospace' }} />
                          )}
                          <Chip
                            size="small"
                            label={activeGroup.delivery_type === 'ILT' ? 'Instructor-led' : 'Self-paced'}
                            color={activeGroup.delivery_type === 'SPT' ? 'success' : 'primary'}
                          />
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          Editions — select one tab (only that edition is shown)
                        </Typography>
                        <Tabs
                          value={editionTab}
                          onChange={(_, id) => {
                            const ed = activeGroup.editions.find((e) => e.id === id);
                            if (ed) selectEdition(ed);
                          }}
                          variant="scrollable"
                          scrollButtons="auto"
                          sx={{ mb: 1.5, minHeight: 40 }}
                        >
                          {activeGroup.editions.map((t) => {
                            const code = t.course_code || activeGroup.course_code;
                            return (
                            <Tab
                              key={t.id}
                              value={t.id}
                              label={
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <span>{formatCourseEdition(t.edition_number) || 'Draft'}</span>
                                  {code ? (
                                    <Chip
                                      size="small"
                                      label={code}
                                      sx={{ height: 18, fontFamily: 'monospace', fontSize: '0.65rem', bgcolor: 'rgba(255,255,255,0.35)' }}
                                    />
                                  ) : null}
                                </Stack>
                              }
                              sx={COURSE_TAB_SX.edition}
                            />
                            );
                          })}
                        </Tabs>
                        {(() => {
                          const selected =
                            activeGroup.editions.find((e) => e.id === editionTab) ||
                            activeGroup.editions[activeGroup.editions.length - 1];
                          return selected ? renderEditionRow(selected) : null;
                        })()}
                      </>
                    )}
                  </Box>
                );
              })()
            ) : (
              groupTitlesByVersionGroup(titles).map((group) => {
                const selectedInGroup = group.editions.some((e) => e.id === expandedTitleId);
                return (
                  <Accordion
                    key={group.groupId}
                    expanded={selectedInGroup}
                    onChange={(_, isExpanded) => {
                      if (isExpanded) {
                        const pick =
                          group.editions.find((e) => e.id === expandedTitleId) ||
                          group.editions.find((e) => e.status === 'published') ||
                          group.editions[group.editions.length - 1];
                        if (pick) selectEdition(pick);
                      } else if (selectedInGroup) {
                        setExpandedTitleId(null);
                        setDetailTitle(null);
                      }
                    }}
                    sx={{
                      mb: 1.25,
                      '&:before': { display: 'none' },
                      borderRadius: '10px !important',
                      overflow: 'hidden',
                      bgcolor: selectedInGroup ? '#FFF8E7' : 'rgba(255,255,255,0.85)',
                      border: selectedInGroup ? '2px solid #E6A817' : '1px solid #C5E8D1',
                      boxShadow: selectedInGroup ? '0 0 0 1px rgba(230,168,23,0.25)' : 'none'
                    }}
                  >
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', pr: 1 }}>
                        <Typography fontWeight={700}>{group.name}</Typography>
                        {group.course_code && (
                          <Chip size="small" variant="outlined" label={group.course_code} sx={{ fontFamily: 'monospace' }} />
                        )}
                        <Chip
                          size="small"
                          label={group.delivery_type === 'ILT' ? 'Instructor-led' : 'Self-paced'}
                          color={group.delivery_type === 'SPT' ? 'success' : 'primary'}
                        />
                        <Chip
                          size="small"
                          variant="outlined"
                          label={formatEditionVersionLabel(group.editions)}
                        />
                      </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                        Versions / editions — use copy icon to create another
                      </Typography>
                      <Stack spacing={1}>{group.editions.map((t) => renderEditionRow(t))}</Stack>
                    </AccordionDetails>
                  </Accordion>
                );
              })
            )}
          </CardContent>
        </Card>
      )}

      {detailTitle && expandedTitleId === detailTitle.id && (
        <Card variant="outlined" sx={{ borderRadius: 2, mb: 1, borderColor: '#C8E6C9' }}>
          <CardContent sx={{ pb: 1 }}>
            <Tabs
              value={courseWorkspaceTab}
              onChange={(_, v) => setCourseWorkspaceTab(v)}
              sx={{ mb: 1, minHeight: 48 }}
            >
              <Tab value="details" label="Course details" sx={COURSE_TAB_SX.workspace} />
              <Tab value="outline" label="Course outline" sx={COURSE_TAB_SX.outline} />
            </Tabs>
            {courseWorkspaceTab === 'details' && (
              <Stack spacing={1.5}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  {detailTitle.name}
                  {formatCourseEdition(detailTitle.edition_number)
                    ? ` (${formatCourseEdition(detailTitle.edition_number)})`
                    : ''}
                </Typography>
                <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
                  <Chip
                    size="small"
                    label={STATUS_LABELS[detailTitle.status] || detailTitle.status}
                    color={
                      detailTitle.status === 'published'
                        ? 'success'
                        : detailTitle.status === 'admin_blocked'
                          ? 'error'
                          : detailTitle.status === 'ready_to_preview'
                            ? 'warning'
                            : 'default'
                    }
                  />
                  {detailTitle.course_code && (
                    <Chip size="small" variant="outlined" label={detailTitle.course_code} sx={{ fontFamily: 'monospace' }} />
                  )}
                  <Chip
                    size="small"
                    variant="outlined"
                    label={
                      OUTLINE_FORMAT_TABS.find((x) => x.value === (detailTitle.outline_format || 'sequence'))?.label ||
                      'Sequence Format'
                    }
                  />
                </Stack>
                {detailTitle.status === 'admin_blocked' && (
                  <Alert severity="error">
                    Admin Blocked it — catalog visibility is off. Log a Helpdesk ticket to work with admin to unblock.
                    <Button
                      size="small"
                      sx={{ ml: 1 }}
                      onClick={() =>
                        navigate('/helpdesk/new', {
                          state: {
                            ticketDraft: {
                              subject: `Unblock training: ${detailTitle.name} (${formatCourseEdition(detailTitle.edition_number) || 'Draft'})`,
                              description: [
                                'Please unblock this course edition.',
                                `Course: ${detailTitle.name}`,
                                `Course ID: ${detailTitle.course_code || '—'}`,
                                `Title id: ${detailTitle.id}`,
                                'Status: Admin Blocked it'
                              ].join('\n')
                            }
                          }
                        })
                      }
                    >
                      Log ticket
                    </Button>
                  </Alert>
                )}
                <Stack direction="row" flexWrap="wrap" gap={1} alignItems="center">
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<EditIcon />}
                    onClick={() => openEditTitle(detailTitle)}
                  >
                    Edit course
                  </Button>
                  {detailTitle.status === 'draft' && (
                    <Button size="small" variant="contained" onClick={() => setCourseStatus('ready_to_preview')}>
                      Save course (ready to preview)
                    </Button>
                  )}
                  {(detailTitle.status === 'ready_to_preview' || detailTitle.status === 'draft') && (
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<ViewIcon />}
                      onClick={() => openCoursePreview(detailTitle.id)}
                    >
                      Preview
                    </Button>
                  )}
                  {(detailTitle.status === 'draft' || detailTitle.status === 'ready_to_preview') && (
                    <Button size="small" variant="contained" color="success" onClick={() => setCourseStatus('published')}>
                      Publish
                    </Button>
                  )}
                  {detailTitle.status === 'ready_to_preview' && (
                    <Alert severity="info" sx={{ py: 0, flex: 1, minWidth: 200 }}>
                      Ready to preview — use <strong>Preview</strong> to review this edition before publishing.
                    </Alert>
                  )}
                  <Button size="small" variant="outlined" color="secondary" onClick={() => setCourseWorkspaceTab('outline')}>
                    Open course outline
                  </Button>
                </Stack>
              </Stack>
            )}
          </CardContent>
        </Card>
      )}

      {detailTitle && expandedTitleId === detailTitle.id && courseWorkspaceTab === 'outline' && (
        <Card id="training-course-outline" variant="outlined" sx={{ borderRadius: 2, mb: 1, scrollMarginTop: 16, borderColor: '#CE93D8', bgcolor: '#FAF5FC' }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
              <OutlineIcon sx={{ color: '#6A1B9A' }} />
              <Typography variant="subtitle1" sx={{ color: '#4A148C', fontWeight: 700 }}>
                Course outline — {detailTitle.name}
                {formatCourseEdition(detailTitle.edition_number)
                  ? ` (${formatCourseEdition(detailTitle.edition_number)})`
                  : ''}
              </Typography>
              {detailTitle.course_code && (
                <Chip size="small" variant="outlined" label={detailTitle.course_code} sx={{ fontFamily: 'monospace' }} />
              )}
              <Chip
                size="small"
                color="secondary"
                label={
                  OUTLINE_FORMAT_TABS.find((x) => x.value === outlineFormatTab)?.label || 'Sequence Format'
                }
              />
            </Stack>
            <Alert severity="info" sx={{ mb: 2 }}>
              Training Mode for this course is <strong>{OUTLINE_FORMAT_TABS.find((x) => x.value === outlineFormatTab)?.label || 'Sequence Format'}</strong>.
              Change it under <strong>Edit course → Training Mode</strong>. This outline shows that format only.
            </Alert>
            {OUTLINE_FORMAT_TABS.find((x) => x.value === outlineFormatTab)?.slotNoun && (
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }} flexWrap="wrap">
                <TextField
                  size="small"
                  type="number"
                  label={`${OUTLINE_FORMAT_TABS.find((x) => x.value === outlineFormatTab).slotNoun} #`}
                  value={scheduleSlotDraft}
                  onChange={(e) => setScheduleSlotDraft(e.target.value)}
                  inputProps={{ min: 1 }}
                  sx={{ width: 140 }}
                  helperText="New content attaches to this slot"
                />
                <Typography variant="caption" color="text.secondary">
                  Daily → day buckets, Weekly → week buckets, Monthly → month buckets, Conference/Discussion → session/topic buckets.
                </Typography>
              </Stack>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              <strong># / Seq</strong> is always outline order. Placeholders stay as slot markers — add items after them. Then save / publish this edition.
            </Typography>
            {loadingDetail ? <CircularProgress size={28} /> : outlineTable}
          </CardContent>
        </Card>
      )}

      {detailTitle && expandedTitleId === detailTitle.id && courseWorkspaceTab === 'outline' && (
        <Card variant="outlined" sx={{ borderRadius: 2, mb: 1 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Course feedback (instructor review)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Learners submit 5★ ratings, optional thumbs, and comments. Status starts as <strong>Submitted</strong>; approve to{' '}
              <strong>Approved</strong> so it appears under the course publicly.
            </Typography>
            {portalFeedbackLoading ? (
              <CircularProgress size={28} />
            ) : portalFeedbackRows.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No feedback submitted yet.
              </Typography>
            ) : (
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Learner</TableCell>
                      <TableCell align="right">Stars</TableCell>
                      <TableCell>Thumb</TableCell>
                      <TableCell>Comment</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {portalFeedbackRows.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell>
                          {row.submitter
                            ? `${row.submitter.first_name || ''} ${row.submitter.last_name || ''}`.trim() ||
                              row.submitter.email
                            : '—'}
                        </TableCell>
                        <TableCell align="right">{row.rating}/5</TableCell>
                        <TableCell>
                          {row.thumb === 'up' ? '👍' : row.thumb === 'down' ? '👎' : '—'}
                        </TableCell>
                        <TableCell sx={{ maxWidth: 280, whiteSpace: 'pre-wrap' }}>{row.comment || '—'}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={
                              row.review_status === 'accepted'
                                ? 'Approved'
                                : row.review_status === 'rejected'
                                  ? 'Rejected'
                                  : row.review_status === 'pending'
                                    ? 'Submitted'
                                    : row.review_status || 'legacy'
                            }
                            color={
                              row.review_status === 'accepted'
                                ? 'success'
                                : row.review_status === 'rejected'
                                  ? 'default'
                                  : row.review_status === 'pending'
                                    ? 'warning'
                                    : 'default'
                            }
                          />
                        </TableCell>
                        <TableCell align="right">
                          {row.review_status === 'pending' ? (
                            <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                              <Button size="small" variant="contained" color="success" onClick={() => reviewPortalFeedback(row.id, 'accepted')}>
                                Approve
                              </Button>
                              <Button size="small" onClick={() => reviewPortalFeedback(row.id, 'rejected')}>
                                Reject
                              </Button>
                            </Stack>
                          ) : (
                            '—'
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog open={spotDialog} onClose={() => setSpotDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingSpotId ? 'Edit sub-category' : 'New sub-category'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth margin="normal" label="Name" value={spotForm.name} onChange={(e) => setSpotForm({ ...spotForm, name: e.target.value })} />
          <TextField
            fullWidth
            margin="normal"
            label="Description"
            multiline
            minRows={2}
            value={spotForm.description}
            onChange={(e) => setSpotForm({ ...spotForm, description: e.target.value })}
          />
          <TextField
            fullWidth
            margin="normal"
            type="number"
            label="Sort order"
            value={spotForm.sort_order}
            onChange={(e) => setSpotForm({ ...spotForm, sort_order: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSpotDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveSpot}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={publishedEditLock.open}
        onClose={() => setPublishedEditLock({ open: false, title: null })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Course cannot be edited</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            This course is <strong>published</strong> and locked for edits
            {publishedEditLock.title?.name ? (
              <>
                {' '}
                (<strong>{publishedEditLock.title.name}</strong>
                {formatCourseEdition(publishedEditLock.title.edition_number)
                  ? ` · ${formatCourseEdition(publishedEditLock.title.edition_number)}`
                  : ''}
                )
              </>
            ) : null}
            .
          </Alert>
          <Typography variant="body2" paragraph>
            To change this edition, unpublish it first. Instructors can also create a <strong>new edition</strong> anytime
            (draft copy) and publish that instead of changing the live catalog version.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1, flexWrap: 'wrap' }}>
          <Button onClick={() => setPublishedEditLock({ open: false, title: null })}>Close</Button>
          <Button
            variant="outlined"
            startIcon={<NewEditionIcon />}
            disabled={(publishedEditLock.title?.edition_number || 0) >= 9999}
            onClick={() => {
              const id = publishedEditLock.title?.id;
              setPublishedEditLock({ open: false, title: null });
              if (id) publishNewEdition(id);
            }}
          >
            New edition
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => unpublishCourse(publishedEditLock.title)}
          >
            Unpublish
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={titleDialog} onClose={() => setTitleDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingTitleId ? 'Edit course' : 'Add course'}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1, mb: 0.5 }}>
            Same fields for create and edit. Save to update schedule, owners, and status for this version.
          </Typography>
          <TextField
            fullWidth
            margin="normal"
            label="Course name"
            required
            value={titleForm.name}
            onChange={(e) => setTitleForm({ ...titleForm, name: e.target.value })}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Delivery</InputLabel>
            <Select
              label="Delivery"
              value={titleForm.delivery_type}
              onChange={(e) =>
                setTitleForm({
                  ...titleForm,
                  delivery_type: e.target.value,
                  schedule_model: e.target.value === 'SPT' ? 'none' : titleForm.schedule_model === 'none' ? 'weekly' : titleForm.schedule_model
                })
              }
            >
              <MenuItem value="SPT">Self paced (SPT) — start/end anytime</MenuItem>
              <MenuItem value="ILT">Instructor led (ILT) — schedule & times</MenuItem>
            </Select>
          </FormControl>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enUS}>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="Start date (optional)"
                value={parseFormDate(titleForm.start_date)}
                onChange={(d) => setTitleForm({ ...titleForm, start_date: formatFormDate(d) })}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    margin: 'normal',
                    helperText: 'Click the field or calendar icon to pick a date',
                  },
                  openPickerButton: { 'aria-label': 'Open start date calendar' },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DatePicker
                label="End date (optional)"
                value={parseFormDate(titleForm.end_date)}
                onChange={(d) => setTitleForm({ ...titleForm, end_date: formatFormDate(d) })}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    margin: 'normal',
                    helperText: 'Optional — outline # is still just order',
                  },
                  openPickerButton: { 'aria-label': 'Open end date calendar' },
                }}
              />
            </Grid>
          </Grid>
          <FormControl fullWidth margin="normal">
            <InputLabel>Training Mode</InputLabel>
            <Select
              label="Training Mode"
              value={titleForm.outline_format || 'sequence'}
              onChange={(e) => setTitleForm({ ...titleForm, outline_format: e.target.value })}
            >
              {OUTLINE_FORMAT_TABS.map((x) => (
                <MenuItem key={x.value} value={x.value}>
                  {x.label}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              Controls how the course outline is structured. Outline shows only this mode.
            </FormHelperText>
          </FormControl>
          <FormControl fullWidth margin="normal" disabled={titleForm.delivery_type === 'SPT'}>
            <InputLabel>Live schedule</InputLabel>
            <Select
              label="Live schedule"
              value={titleForm.delivery_type === 'SPT' ? 'none' : titleForm.schedule_model || 'weekly'}
              onChange={(e) => setTitleForm({ ...titleForm, schedule_model: e.target.value })}
            >
              {SCHEDULE_MODELS.map((x) => (
                <MenuItem key={x.v} value={x.v}>
                  {x.l}
                </MenuItem>
              ))}
            </Select>
            <FormHelperText>
              {titleForm.delivery_type === 'SPT'
                ? 'Self-paced courses do not use a live class schedule.'
                : 'Optional live session cadence (daily / weekly / …) for instructor-led delivery.'}
            </FormHelperText>
          </FormControl>
          {titleForm.delivery_type === 'ILT' && titleForm.schedule_model === 'weekly' && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Class days (select all that apply)
              </Typography>
              <Stack direction="row" flexWrap="wrap" gap={0.75}>
                {WEEKDAY_CHIPS.map((d) => {
                  const on = titleForm.weekdays.includes(d.v);
                  return (
                    <Chip
                      key={d.v}
                      label={d.short}
                      color={on ? 'primary' : 'default'}
                      variant={on ? 'filled' : 'outlined'}
                      onClick={() =>
                        setTitleForm({
                          ...titleForm,
                          weekdays: on ? titleForm.weekdays.filter((x) => x !== d.v) : [...titleForm.weekdays, d.v].sort((a, b) => a - b)
                        })
                      }
                      sx={{ fontWeight: on ? 600 : 400 }}
                    />
                  );
                })}
              </Stack>
            </Box>
          )}
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TimePicker
                label="Session start"
                value={parseFormTime(titleForm.session_start_time)}
                onChange={(d) => setTitleForm({ ...titleForm, session_start_time: formatFormTime(d) })}
                ampm={false}
                disabled={titleForm.delivery_type === 'SPT'}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    margin: 'normal',
                    helperText: 'Digital clock — click field or icon to set time',
                  },
                  openPickerButton: { 'aria-label': 'Open session start time picker' },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TimePicker
                label="Session end"
                value={parseFormTime(titleForm.session_end_time)}
                onChange={(d) => setTitleForm({ ...titleForm, session_end_time: formatFormTime(d) })}
                ampm={false}
                disabled={titleForm.delivery_type === 'SPT'}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    margin: 'normal',
                    helperText: 'Digital clock — click field or icon to set time',
                  },
                  openPickerButton: { 'aria-label': 'Open session end time picker' },
                }}
              />
            </Grid>
          </Grid>
          </LocalizationProvider>
          <FormControl fullWidth margin="normal">
            <InputLabel>Title owner</InputLabel>
            <Select label="Title owner" value={titleForm.owner_user_id} onChange={(e) => setTitleForm({ ...titleForm, owner_user_id: e.target.value })}>
              <MenuItem value="">
                <em>Me (current user)</em>
              </MenuItem>
              {(users || []).map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.first_name} {u.last_name} ({u.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Instructors</InputLabel>
            <Select
              multiple
              label="Instructors"
              value={titleForm.instructor_user_ids}
              onChange={(e) =>
                setTitleForm({
                  ...titleForm,
                  instructor_user_ids: typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value
                })
              }
              input={<OutlinedInput label="Instructors" />}
              renderValue={(sel) =>
                (users || [])
                  .filter((u) => sel.includes(u.id))
                  .map((u) => `${u.first_name} ${u.last_name}`)
                  .join(', ')
              }
            >
              {(users || []).map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.first_name} {u.last_name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select label="Status" value={titleForm.status} onChange={(e) => setTitleForm({ ...titleForm, status: e.target.value })}>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="ready_to_preview">Ready to preview</MenuItem>
              <MenuItem value="published">Published</MenuItem>
              <MenuItem value="archived">Archived</MenuItem>
              <MenuItem value="admin_blocked">Admin Blocked it</MenuItem>
            </Select>
            <FormHelperText>
              <strong>Draft</strong> — editing. <strong>Ready to preview</strong> — stable for review, not in catalog.{' '}
              <strong>Published</strong> — appears in Browse Catalog.
            </FormHelperText>
          </FormControl>
          <FormControlLabel
            sx={{ mt: 1, display: 'flex', alignItems: 'flex-start' }}
            control={
              <Checkbox
                checked={!!titleForm.allow_drop_from_enrollment}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setTitleForm({
                    ...titleForm,
                    allow_drop_from_enrollment: checked,
                    drop_window_days: checked
                      ? titleForm.drop_window_days || 14
                      : titleForm.drop_window_days
                  });
                }}
              />
            }
            label="Allow user to drop from enrollment"
          />
          <FormHelperText sx={{ mt: 0, mb: 1 }}>
            When checked, learners see <strong>Drop from Enrollment</strong> for a limited window after they enroll in this course.
          </FormHelperText>
          <TextField
            fullWidth
            margin="normal"
            type="number"
            label="Drop window (days from enrollment)"
            value={titleForm.drop_window_days}
            disabled={!titleForm.allow_drop_from_enrollment}
            onChange={(e) => setTitleForm({ ...titleForm, drop_window_days: e.target.value })}
            inputProps={{ min: 1, max: 365 }}
            helperText="Default is 14 days from the date the user enrolled in this course."
          />
          <TextField
            fullWidth
            margin="normal"
            type="number"
            label="Max enroll / drop / reenroll cycles"
            value={titleForm.max_enroll_drop_cycles}
            onChange={(e) => setTitleForm({ ...titleForm, max_enroll_drop_cycles: e.target.value })}
            inputProps={{ min: 1, max: 50 }}
            helperText="How many times a learner may enroll in this course (including reenroll after drop or after complete). Example: 2 = enroll, drop, reenroll once more."
          />
          <TextField
            fullWidth
            margin="normal"
            type="number"
            label="Sort order"
            value={titleForm.sort_order}
            onChange={(e) => setTitleForm({ ...titleForm, sort_order: e.target.value })}
            helperText="Lower numbers appear first in the courses list."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTitleDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveTitle}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={inlineMatOpen} onClose={() => setInlineMatOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add content to outline</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Creates a <strong>material</strong> in this category (and a TRG-… reference code), then attaches it to this course. Also listed on the Materials tab.
          </Typography>
          <TextField
            fullWidth
            margin="normal"
            label="Title"
            required
            value={matForm.title}
            onChange={(e) => setMatForm({ ...matForm, title: e.target.value })}
          />
          <TextField
            fullWidth
            margin="normal"
            label="Description"
            multiline
            minRows={2}
            value={matForm.description}
            onChange={(e) => setMatForm({ ...matForm, description: e.target.value })}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Type</InputLabel>
            <Select
              label="Type"
              value={matForm.content_type}
              onChange={(e) => setMatForm({ ...matForm, content_type: e.target.value, content_url: '' })}
            >
              {TRAINING_MATERIAL_CONTENT_TYPES.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {matForm.content_type !== 'video' && CONTENT_TYPES_NEEDING_URL.has(matForm.content_type) && (
            <TextField
              fullWidth
              margin="normal"
              label={matForm.content_type === 'youtube' ? 'YouTube URL' : 'URL or link'}
              value={matForm.content_url}
              onChange={(e) => setMatForm({ ...matForm, content_url: e.target.value })}
            />
          )}
          {matForm.content_type === 'video' && (
            <>
              <Button variant="outlined" component="label" sx={{ mt: 1, mr: 1 }}>
                Upload video file
                <input type="file" accept="video/*" hidden onChange={(e) => setMatFile(e.target.files?.[0] || null)} />
              </Button>
              {matFile && (
                <Typography variant="caption" display="block">
                  {matFile.name}
                </Typography>
              )}
              <TextField
                fullWidth
                margin="normal"
                label="Video URL (optional if you uploaded a file)"
                value={matForm.content_url}
                onChange={(e) => setMatForm({ ...matForm, content_url: e.target.value })}
              />
            </>
          )}
          <TextField
            fullWidth
            margin="normal"
            type="number"
            label="Duration (minutes)"
            value={matForm.duration_minutes}
            onChange={(e) => setMatForm({ ...matForm, duration_minutes: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInlineMatOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={saveInlineMaterial}>
            Add to outline
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!editOutlineItem} onClose={() => setEditOutlineItem(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Edit row label</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            margin="normal"
            label="Label / title shown in outline"
            value={editOutlineLabel}
            onChange={(e) => setEditOutlineLabel(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOutlineItem(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveEditOutlineItem}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={examDialog} onClose={() => { setExamDialog(false); setExamInsertAfter(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>{examInsertAfter === null ? 'Add exam at end' : `Insert exam after row ${examInsertAfter + 1}`}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Only exams scoped to this category, sub-category, and course (or broader) are listed — so picks match server rules. Create exams in the <strong>Exams</strong> tab if the list is empty.
          </Typography>
          <FormControl fullWidth margin="normal">
            <InputLabel>Exam</InputLabel>
            <Select label="Exam" value={selectedExamId} onChange={(e) => setSelectedExamId(e.target.value)}>
              <MenuItem value="">Select…</MenuItem>
              {linkableExams.length === 0 ? (
                <MenuItem value="" disabled>
                  No exams available (all may already be on this outline)
                </MenuItem>
              ) : (
                linkableExams.map((ex) => (
                  <MenuItem key={ex.id} value={ex.id}>
                    {ex.name} ({ex.status || 'draft'})
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setExamDialog(false); setExamInsertAfter(null); }}>Cancel</Button>
          <Button variant="contained" onClick={addExamToOutline} disabled={!selectedExamId}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={breakDialog}
        onClose={() => {
          setBreakDialog(false);
          setBreakInsertAfter(null);
        }}
      >
        <DialogTitle>
          {breakInsertAfter === null ? 'Add break at end' : `Insert break after row ${breakInsertAfter + 1}`}
        </DialogTitle>
        <DialogContent>
          <TextField label="Label" fullWidth margin="normal" value={breakForm.label} onChange={(e) => setBreakForm({ ...breakForm, label: e.target.value })} />
          <TextField
            label="Duration"
            type="number"
            fullWidth
            margin="normal"
            value={breakForm.duration_value}
            onChange={(e) => setBreakForm({ ...breakForm, duration_value: e.target.value })}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Unit</InputLabel>
            <Select label="Unit" value={breakForm.duration_unit} onChange={(e) => setBreakForm({ ...breakForm, duration_unit: e.target.value })}>
              <MenuItem value="hours">hours</MenuItem>
              <MenuItem value="days">days</MenuItem>
              <MenuItem value="weeks">weeks</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setBreakDialog(false);
              setBreakInsertAfter(null);
            }}
          >
            Cancel
          </Button>
          <Button variant="contained" onClick={addBreak}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={linkMatDialog} onClose={() => { setLinkMatDialog(false); setLinkInsertAfter(null); }}>
        <DialogTitle>Link existing material</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Inserts into the selected outline slot. Pick from the <strong>Materials</strong> library for this category (already-linked materials are hidden).
          </Typography>
          <FormControl fullWidth margin="normal">
            <InputLabel>Material (this category)</InputLabel>
            <Select label="Material (this category)" value={linkMaterialId} onChange={(e) => setLinkMaterialId(e.target.value)}>
              {linkableMaterials.length === 0 ? (
                <MenuItem value="" disabled>
                  No materials available (all may already be on this outline)
                </MenuItem>
              ) : (
                linkableMaterials.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.title}
                  </MenuItem>
                ))
              )}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLinkMatDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={linkMaterial} disabled={!linkMaterialId}>
            Link
          </Button>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={addMenuAnchor}
        open={Boolean(addMenuAnchor)}
        onClose={closeAddMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            closeAddMenu();
            openAddContent(addMenuAfterIdx);
          }}
        >
          <ListItemIcon>
            <MaterialAddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="New material"
            secondary={addMenuAfterIdx != null ? 'Add to this outline slot' : 'YouTube, video, PDF, link…'}
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeAddMenu();
            openLinkExisting(addMenuAfterIdx);
          }}
        >
          <ListItemIcon>
            <LibraryIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Link library material"
            secondary={addMenuAfterIdx != null ? 'Add to this outline slot' : undefined}
          />
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeAddMenu();
            addLiveVideoCall(addMenuAfterIdx);
          }}
        >
          <ListItemIcon>
            <VideoCallIcon fontSize="small" color="secondary" />
          </ListItemIcon>
          <ListItemText primary="Join Video Call" secondary="Live Meet session for instructors / learners" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeAddMenu();
            openExamDialog(addMenuAfterIdx);
          }}
        >
          <ListItemIcon>
            <ExamOutlineIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Exam" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeAddMenu();
            openBreakDialog(addMenuAfterIdx);
          }}
        >
          <ListItemIcon>
            <BreakIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Break" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeAddMenu();
            addPlaceholderAfter(addMenuAfterIdx);
          }}
        >
          <ListItemIcon>
            <PlaceholderIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="Placeholder (slot marker)" />
        </MenuItem>
      </Menu>

      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="md" fullWidth scroll="paper">
        <DialogTitle>
          Preview — {detailTitle?.name || 'Course'}
          {formatCourseEdition(detailTitle?.edition_number)
            ? ` (${formatCourseEdition(detailTitle.edition_number)})`
            : ''}
        </DialogTitle>
        <DialogContent dividers>
          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }} useFlexGap>
            <Chip size="small" label={STATUS_LABELS[detailTitle?.status] || detailTitle?.status || '—'} />
            {detailTitle?.course_code && (
              <Chip size="small" variant="outlined" label={detailTitle.course_code} sx={{ fontFamily: 'monospace' }} />
            )}
            <Chip
              size="small"
              variant="outlined"
              label={
                OUTLINE_FORMAT_TABS.find((x) => x.value === (detailTitle?.outline_format || 'sequence'))?.label ||
                'Sequence Format'
              }
            />
          </Stack>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Outline ({outlineAnchors(detailTitle?.contentItems || []).length} slots)
          </Typography>
          {outlineAnchors(detailTitle?.contentItems || []).length === 0 ? (
            <Typography color="text.secondary">No outline items yet.</Typography>
          ) : (
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={48}>#</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Summary</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {outlineAnchors(detailTitle.contentItems || []).map((anchor, idx) => {
                    const slotItems = getSlotAttachments(detailTitle.contentItems || [], anchor);
                    return (
                    <TableRow key={anchor.id}>
                      <TableCell>{idx + 1}</TableCell>
                      <TableCell>{slotItems.length > 1 ? `Multiple (${slotItems.length})` : slotItems[0]?.item_type || anchor.item_type}</TableCell>
                      <TableCell>
                        {slotItems.map((it) => it.label || it.material?.title || it.exam?.name || it.content_url || '—').join(' · ')}
                        {anchor.schedule_label ? ` · ${anchor.schedule_label}` : ''}
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreviewOpen(false)}>Close</Button>
          {detailTitle?.status === 'ready_to_preview' && (
            <Button
              variant="contained"
              color="success"
              onClick={() => {
                setPreviewOpen(false);
                setCourseStatus('published');
              }}
            >
              Publish
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LearningSpotsCourseAdmin;
